import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SaleListingsResponse } from '../../shared/intelligence/types.js';
import { PropertyDetails } from '../types/listings';

export function useSaleListings() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Handle unauthorized responses
  const handleUnauthorizedResponse = (response: Response) => {
    if (response.status === 401) {
      // Redirect to login page
      navigate('/login');
      return true;
    }
    return false;
  };

  // Transform sale listing data to PropertyDetails format
  const transformSaleDataToPropertyDetails = (saleData: SaleListingsResponse): PropertyDetails | null => {
    if (!saleData.success || !saleData.data) {
      return null;
    }

    // Normalize data to always be an array (defensive against cache inconsistencies)
    const dataArray = Array.isArray(saleData.data) ? saleData.data : [saleData.data];

    if (dataArray.length === 0) {
      return null;
    }

    // Use the first property in the results (could be enhanced to select best match)
    const property = dataArray[0];

    // Validate required fields exist
    if (!property || property.bedrooms === null || property.bathrooms === null ||
        property.squareFootage === null || property.price === null) {
      console.warn('Sale listing missing required property details');
      return null;
    }

    return {
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      sqft: property.squareFootage,
      price: property.price
    };
  };

  const fetchSaleListingsByAddress = async (address: string, signal?: AbortSignal): Promise<PropertyDetails | null> => {
    if (!address || address.trim().length === 0) {
      return null;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Build API URL with address parameter
      const apiUrl = new URL('/api/properties/listings/sale', window.location.origin);
      apiUrl.searchParams.set('address', address.trim());

      const response = await fetch(apiUrl.toString(), { signal });

      if (handleUnauthorizedResponse(response)) {
        return null;
      }

      if (!response.ok) {
        // If 404, no listings found - this is not an error, just return null
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: SaleListingsResponse = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch sale listings');
      }

      return transformSaleDataToPropertyDetails(data);
    } catch (error) {
      // Don't set error state for aborted requests
      if (error instanceof Error && error.name === 'AbortError') {
        return null;
      }
      
      console.error('Error fetching sale listings:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch sale listings';
      setError(errorMessage);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    error,
    fetchSaleListingsByAddress,
    transformSaleDataToPropertyDetails
  };
}